import i18nFactory from '@yandex-int/i18n';
import * as React from 'react';
import { Href } from 'redux-little-router';

import { ProductsSearchForm } from '../../containers/ProductsSearchForm/ProductsSearchForm';
import { getProductsListItems } from '../../declarations/productsList/productsList';
import { Bem } from '../../service/Bem/Bem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { AccessRights } from '../../typings/common';
import { SortParams } from '../../typings/grid';
import {
    NewProduct,
    ProductsForm as ProductsFormType,
    ProductsListCellItem,
    ProductsPageStates
} from '../../typings/products';
import { AutoSizedGrid } from '../AutoSizedGrid/AutoSizedGrid';
import {
    CommonGridScreenContext,
    CommonGridScreenContextProps
} from '../CommonGridScreen/CommonGridScreen';
import { CommonGridScreen } from '../CommonGridScreen/CommonGridScreen';
import { Loader } from '../Loader/Loader';
import { PageCommonHeader } from '../PageCommonHeader/PageCommonHeader';
import { ProductsAddForm } from '../ProductsAddForm/ProductsAddForm';
import { ScreenErrorMessage } from '../ScreenErrorMessage/ScreenErrorMessage';

import './Products.css';
import * as keyset from './Products.i18n';

const i18n = i18nFactory(keyset);

const BLOCK = 'products';
const ROW_HEIGHT = 75;

export interface ProductsProps {
    ulogin: string;
    products: ProductsListCellItem[];
    sortParams?: SortParams;
    pageStatus: ProductsPageStates;
    productsForm: ProductsFormType;
    onAddProduct: (product: NewProduct) => void;
    onSortData: (sortParams: SortParams) => void;
    onChangeProductsForm: (productsFormData: ProductsFormType) => void;
    onTableScrollStart: () => void;
    onTableScrollEnd: () => void;
    onSearch: (searchInput: string) => void;
    accessRights: AccessRights;
}

export const getProductsTitle = (access: AccessRights): string => {
    if (access.canManageInternalAdAccessRights) {
        return i18n('marketers-title');
    }
    return i18n('default-title');
};

export class Products extends React.Component<ProductsProps> {
    componentDidUpdate(prevProps: ProductsProps): void {
        const { onSortData, sortParams } = this.props;
        const { sortParams: prevSortParams } = prevProps;

        if (
            sortParams &&
            (!prevSortParams ||
                sortParams.direction !== prevSortParams.direction ||
                sortParams.key !== prevSortParams.key)
        ) {
            onSortData(sortParams);
        }
    }

    render() {
        const { pageStatus } = this.props;
        if (pageStatus === ProductsPageStates.LOADING) {
            return <Loader size={'l'} text={i18n('loading')} />;
        }

        if (pageStatus === ProductsPageStates.ERROR) {
            return (
                <ScreenErrorMessage
                    cls={Bem.cls(BLOCK, 'error')}
                    header={i18n('error-title')}
                    text={i18n('error-text')}
                />
            );
        }

        return (
            <CommonGridScreen
                header={this.renderHead()}
                body={this.renderBody()}
            />
        );
    }

    private renderHead = () => {
        const {
            onAddProduct,
            pageStatus,
            productsForm,
            onChangeProductsForm,
            onSearch,
            accessRights
        } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'header-area')}>
                <div className={Bem.cls(BLOCK, 'section')}>
                    <div className={Bem.cls(BLOCK, 'section-left')}>
                        <div className={Bem.cls(BLOCK, 'header')}>
                            <PageCommonHeader hasBeta={false}>
                                {getProductsTitle(accessRights)}
                            </PageCommonHeader>

                            <p className={Bem.cls(BLOCK, 'description')}>
                                {accessRights.canManageInternalAdAccessRights
                                    ? i18n('marketers-header-description')
                                    : i18n('default-header-description')}
                            </p>
                        </div>
                        <ProductsSearchForm onSearch={onSearch} />
                    </div>
                    {accessRights.canManageInternalAdAccessRights && (
                        <ProductsAddForm
                            productsForm={productsForm}
                            pageStatus={pageStatus}
                            changeProductsForm={onChangeProductsForm}
                            onAddProduct={onAddProduct}
                        />
                    )}
                </div>
            </div>
        );
    };

    private renderBody = () => {
        const {
            products,
            sortParams,
            onTableScrollStart,
            onTableScrollEnd
        } = this.props;
        const allItemsIds = products.map(item => item.id);

        return (
            <CommonGridScreenContext.Consumer>
                {(data: CommonGridScreenContextProps) => {
                    return (
                        <AutoSizedGrid
                            rowHeight={ROW_HEIGHT}
                            data={products}
                            columns={getProductsListItems()}
                            requestIndex={this.requestIndex}
                            totalCount={products.length}
                            allItemsIds={allItemsIds}
                            sortParams={sortParams}
                            getSortLinkUrl={this.getSortLinkUrl}
                            onTableScrollStart={onTableScrollStart}
                            onTableScrollEnd={onTableScrollEnd}
                            onTableHeightChange={data.onTableHeightChange}
                            gridAdditionalHeight={data.gridAdditionalHeight}
                            onTableScroll={data.onGridScroll}
                            isHorizontalScrollVisible={
                                data.isHorizontalScrollVisible
                            }
                            rowClassName={Bem.cls(BLOCK, 'table-row')}
                            isCompactViewEnabled={false}
                        />
                    );
                }}
            </CommonGridScreenContext.Consumer>
        );
    };

    private requestIndex = (id: number) => {
        const { products } = this.props;

        return products[id];
    };

    private getSortLinkUrl = (sortParams: SortParams): Href => {
        const { ulogin } = this.props;

        return UrlHelpers.createNewDirectLink(
            UrlHelpers.createNewDirectUrlPathByParams('products'),
            {
                ...sortParams,
                ulogin
            }
        );
    };
}
